import { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useCreateCollectionMember,
  useGetCollectionsForDepartment,
  getGetTravellerByIDQueryKey,
  getGetTravellersForDepartmentQueryKey,
  TravellerWithCollectionMembershipsDTO,
} from '@/api/trafikportalen';
import { DepartmentPicker } from '../DepartmentPicker';
import Button from '../core/Button';
import { IconCheck } from '@/icons';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { SelectedDepartment } from '@/types/selectedDepartment';
import { getWriteAccessDepartmentIds } from '@/helpers/departmentPermissions';

interface AddTravellerToDepartmentFormProps {
  traveller: TravellerWithCollectionMembershipsDTO;
  onCancel: () => void;
  onSuccess: () => void;
}

export const AddTravellerToDepartmentForm = ({
  traveller,
  onCancel,
  onSuccess,
}: AddTravellerToDepartmentFormProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const createCollectionMember = useCreateCollectionMember();

  const [selectedDepartment, setSelectedDepartment] =
    useState<SelectedDepartment | null>(null);

  const { data: collections } = useGetCollectionsForDepartment(
    selectedDepartment?.id || '',
    {
      query: {
        enabled: !!selectedDepartment?.id,
      },
    },
  );

  const masterCollection = useMemo(() => {
    if (!collections) {
      return null;
    }
    return collections.find((collection) => collection.type === 'master');
  }, [collections]);

  const currentDepartmentIds = useMemo(() => {
    return traveller.memberships.map((membership) => membership.department_id);
  }, [traveller.memberships]);

  const writeAccessDepartmentIds = useMemo(() => {
    return getWriteAccessDepartmentIds();
  }, []);

  const handleDepartmentSelect = useCallback(
    (department: SelectedDepartment) => {
      setSelectedDepartment(department);
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedDepartment || !traveller.traveller.email) {
      return;
    }

    if (!masterCollection?.id) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage:
              'Kunde inte hitta master-kollektionen för avdelningen',
          }),
        },
        { autoClose: 3000 },
      );
      return;
    }

    try {
      await createCollectionMember.mutateAsync({
        data: {
          email: traveller.traveller.email,
          first_name: traveller.traveller.first_name,
          last_name: traveller.traveller.last_name,
          national_identity_number:
            traveller.traveller.national_identity_number,
          phone: traveller.traveller.phone,
          half_product: false,
          collection_id: masterCollection.id,
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetTravellerByIDQueryKey(traveller.traveller.id),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetTravellersForDepartmentQueryKey(
            selectedDepartment.id,
          ),
        }),
      ]);

      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'Resenären har lagts till i avdelningen',
          }),
        },
        { autoClose: 3000 },
      );

      onSuccess();
    } catch (error) {
      console.error('Error adding traveller to department:', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Ett fel inträffade',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    selectedDepartment,
    masterCollection,
    traveller,
    createCollectionMember,
    queryClient,
    formatMessage,
    onSuccess,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <DepartmentPicker
        title={
          <FormattedMessage defaultMessage="Välj avdelning att lägga till resenären i" />
        }
        onSelect={handleDepartmentSelect}
        nonSelectableDepartments={currentDepartmentIds}
        enabledDepartments={writeAccessDepartmentIds}
      />
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel} variant="ghost">
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          onClick={handleSubmit}
          variant="filled"
          startIcon={<IconCheck />}
          loading={createCollectionMember.isPending}
          disabled={!selectedDepartment || !masterCollection}
        >
          <FormattedMessage defaultMessage="Lägg till" />
        </Button>
      </div>
    </div>
  );
};
